// AddEmailModal.tsx
import React, { useState } from "react";
import axios from "axios";
import { env, useGlobalState } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";
import { getApiUrl } from "../../config";
import { getToken } from "../../helpers/tokenManager";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from "../../components/ui/dialog";
import { Input, Stack, Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";

const AddEmailModal: React.FC = () => {
  // Pull up whether the modal is visible and how to close it
  const { isAddEmailModalVisible, closeAddEmailModal, showErrorMessage } =
    useModalState();

  // We need to refresh account info after adding an email
  const { refreshAccountInfo } = useGlobalState();

  // Local form states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleAddEmail = async () => {
    const bearerToken = getToken();
    if (!bearerToken) {
      showErrorMessage("You need to be logged in to add an email.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${getApiUrl(env)}/v3/apiKey/associate_email`,
        { email, password },
        { headers: { Authorization: `Bearer ${bearerToken}` } },
      );
      if (process.env.NODE_ENV === 'development') console.log("Email associated successfully:", response.data);

      // Refresh user info so the newly added email shows up
      await refreshAccountInfo();

      // Close the modal on success
      closeAddEmailModal();
    } catch (err: any) {
      if (err.response?.data?.message) {
        showErrorMessage(err.response.data.message);
      } else {
        showErrorMessage("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEmail("");
    setPassword("");
    closeAddEmailModal();
  };

  return (
    <DialogRoot lazyMount open={isAddEmailModalVisible} onOpenChange={(e) => { if (!e.open) handleCancel(); }}>
      <DialogContent
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isLoading) {
            e.preventDefault();
            handleAddEmail();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Add email</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Stack gap={4}>
            <Text fontSize="md" color="#52525b" lineHeight="24px">
              Enter the email and password you want to associate:
            </Text>
            <Field label="Email address">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fontSize="sm"
              />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fontSize="sm"
              />
            </Field>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button colorPalette="purple" bg="#9333ea" onClick={handleAddEmail} loading={isLoading}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default AddEmailModal;
